import React, { useMemo } from 'react';
import clsx from 'clsx';
import styles from './SectorIndustryOutlookV2.module.scss';
import GrowArrow from '../../../../../../assets/Svg/GrowArrow';
import greenTick from '../../../../../../assets/green-tick.svg';
import redInfoFull from '../../../../../../assets/red-i-full.svg';
import { SECTOR_INSIGHTS } from './constants';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox/HeaderBodyBox';
import ExpandableText from '../../../../../../common/ExpandableText/ExpandableText';
import SourcesInfoLabel from '../../../../../../common/SourcesInfoLabel/SourcesInfoLabel';
import { OVERVIEW_SOURCE_LABELS } from '../../constants';
import { isEmpty } from '../../../../../../utils/utils';
import { SectorIndustryOutlookV2Props } from './types';
import { toSourceInputs } from './utils';

const SectorIndustryOutlookV2: React.FC<SectorIndustryOutlookV2Props> = ({ sectorOutlook, sectorName }) => {
    const sourceInputs = useMemo(() => toSourceInputs(sectorOutlook?.sources), [sectorOutlook?.sources]);

    if (!sectorOutlook) {
        return null;
    }

    const renderSectorInsights = () => {
        const insightsToRender = SECTOR_INSIGHTS.filter(({ key }) => !isEmpty(sectorOutlook[key]));
        if (isEmpty(insightsToRender)) return null;
        return (
            <div className={styles.sectorInsights}>
                {insightsToRender.map(({ title, key, isRiskIndicator }) => (
                    <div key={key} className={styles.insightItem}>
                        <span className={styles.insightLabel}>{title}</span>
                        {isRiskIndicator ? (
                            <span className={clsx(styles.riskPill, styles[sectorOutlook[key]?.toLowerCase()])}>{sectorOutlook[key]}</span>
                        ) : (
                            <span className={styles.insightValue}>{sectorOutlook[key]}</span>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const renderTrendPill = (text: string, index: number) => (
        <div key={index} className={styles.trendPill}>
            <GrowArrow fill="#545B62" />
            {text}
        </div>
    );

    return (
        <HeaderBodyBox
            title="Sector/Industry Outlook"
            subHeading={sectorName}
            headerRight={<SourcesInfoLabel value={OVERVIEW_SOURCE_LABELS.WEB_SEARCH} sources={sourceInputs} />}
            className={styles.sectorOutlookContainer}
        >
            <div className={styles.sectorOutlookContent}>
                {renderSectorInsights()}

                {!isEmpty(sectorOutlook.sectorOverview) && (
                    <div className={styles.sectorOverview}>
                        <ExpandableText
                            content={sectorOutlook.sectorOverview}
                            expandedContent={
                                !isEmpty(sectorOutlook.recentTrends) && (
                                    <div className={styles.trendsSection}>
                                        <h3 className={styles.sectionTitle}>Recent Trends:</h3>
                                        <div className={styles.pillsContainer}>{sectorOutlook.recentTrends.map(renderTrendPill)}</div>
                                    </div>
                                )
                            }
                        />
                    </div>
                )}

                {(!isEmpty(sectorOutlook.growthDrivers) || !isEmpty(sectorOutlook.keyRisks)) && (
                    <div className={styles.comparisonSection}>
                        {!isEmpty(sectorOutlook.growthDrivers) && (
                            <div className={styles.comparisonColumn}>
                                <div className={styles.columnHeader}>
                                    <img src={greenTick} alt="" className={styles.columnIcon} />
                                    <h3 className={styles.columnTitle}>Growth Drivers:</h3>
                                </div>
                                <ul className={styles.bulletList}>
                                    {sectorOutlook.growthDrivers.map((driver, index) => (
                                        <li key={index}>{driver}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {!isEmpty(sectorOutlook.keyRisks) && (
                            <div className={styles.comparisonColumn}>
                                <div className={styles.columnHeader}>
                                    <img src={redInfoFull} alt="" className={styles.columnIcon} />
                                    <h3 className={styles.columnTitle}>Key Risks</h3>
                                </div>
                                <ul className={styles.bulletList}>
                                    {sectorOutlook.keyRisks.map((risk, index) => (
                                        <li key={index}>{risk}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </HeaderBodyBox>
    );
};

export default SectorIndustryOutlookV2;
